/**
 * 설정 → 시설 정보 「구독 플랜」 칸 (2026-10-27 23시 대표 결정)
 *
 * "기능은 지금 그대로. 무료에서 베이직으로 가려면 각자 계정에서 설정 → 시설에서
 *  구독제 올리는 신청을 해서 올리는 방식으로 해서 동의를 다 받아내자."
 *
 * - 지금 플랜(무료 — 이용 중) · 베이직 요금 안내 · [베이직으로 신청하기]
 * - 신청 창: 이용약관 전문 보기 + 필수 동의 2개(이용약관 · 요금 및 결제 조건) → 신청
 * - 칸을 보일지·신청할 수 있는지는 서버가 정한다(GET /api/v1/owner/subscription-request).
 *   본부 시험 매장·독립 서버(이루는자리)는 칸이 안 보이고, 총무(원장 대리)는 볼 수만 있다.
 * - 🔴 기능 제한 없음 — 신청 여부와 무관하게 기능은 지금 그대로다.
 * - 확인 창에 window.confirm 금지(CLAUDE.md) — 창 안 버튼으로 처리한다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { authedFetch } from '../../../infrastructure/api/authedFetch';
import { SubscriptionPrice, SubscriptionPriceBlock, priceOneLine } from './SubscriptionPriceText';

interface RequestStatus {
  show: boolean;
  can_request?: boolean;
  is_staff_proxy?: boolean;
  version: string;
  effective_date: string;
  plan_name: string;
  fee_label: string;
  price?: SubscriptionPrice | null;
  support_email: string;
  request: {
    status: 'requested' | 'confirmed' | null;
    requested_at: string | null;
    requested_by_name: string | null;
    confirmed_at: string | null;
  };
}

const REQUEST_URL = `${API_BASE_URL}/api/v1/owner/subscription-request`;
export const SUBSCRIPTION_PLAN_ANCHOR = 'subscription-plan';

const formatKoreanDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return `${y}년 ${m}월 ${d}일`;
};

const fmtKst = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const SubscriptionPlanCard: React.FC = () => {
  const [status, setStatus] = useState<RequestStatus | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPricing, setAgreedPricing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    authedFetch(REQUEST_URL)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: RequestStatus | null) => d && setStatus(d))
      .catch(() => {});
  }, []);

  useEffect(load, [load]);

  // 로그인 안내 창의 [지금 신청하러 가기]로 들어오면 이 칸으로 내려 준다
  useEffect(() => {
    if (status?.show && window.location.hash === `#${SUBSCRIPTION_PLAN_ANCHOR}`) {
      document.getElementById(SUBSCRIPTION_PLAN_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [status?.show]);

  if (!status?.show) return null;

  const effective = formatKoreanDate(status.effective_date);
  const req = status.request;

  const submit = async () => {
    if (!agreedTerms || !agreedPricing) return;
    setSaving(true);
    setError('');
    try {
      const r = await authedFetch(REQUEST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agreed_terms: true, agreed_pricing: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : '신청하지 못했습니다. 잠시 뒤 다시 시도해 주세요.');
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '신청하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const openDialog = () => {
    setAgreedTerms(false);
    setAgreedPricing(false);
    setError('');
    setDialogOpen(true);
  };

  return (
    <div id={SUBSCRIPTION_PLAN_ANCHOR} className="bg-white shadow rounded-lg p-6 mb-6 scroll-mt-4">
      <h2 className="text-lg font-medium text-gray-900">💳 구독 플랜</h2>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className={`rounded-lg border p-4 ${req.status ? 'border-gray-200' : 'border-2 border-gray-400'}`}>
          <p className="text-xs text-gray-500">지금 플랜</p>
          <p className="text-lg font-bold text-gray-900 mt-1">무료</p>
          <p className="text-sm text-gray-600 mt-1">{req.status ? `${effective} 전까지 이용` : '이용 중'}</p>
        </div>

        <div className={`rounded-lg p-4 ${req.status ? 'border-2 border-blue-400 bg-blue-50' : 'border border-blue-200 bg-blue-50/50'}`}>
          <p className="text-xs text-blue-700 font-semibold">{status.plan_name}</p>
          {status.price && status.price.discount_amount ? (
            <div className="mt-1">
              <p className="text-sm text-gray-500">
                정가 <span className="line-through">{status.price.list_fee_label}</span>
              </p>
              <p className="text-sm text-rose-700 font-semibold">
                {status.price.discount_label || '할인'} −{status.price.discount_amount.toLocaleString('ko-KR')}원
              </p>
              <p className="text-lg font-bold text-gray-900">{status.price.fee_label}</p>
            </div>
          ) : (
            <p className="text-lg font-bold text-gray-900 mt-1">{status.price?.fee_label || status.fee_label}</p>
          )}
          <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc pl-5">
            <li>약정 없음 · 월 단위 자동 연장</li>
            <li>매월 10일 납부</li>
            <li>카카오 알림톡은 별도 — 건당 13원(부가세 별도)</li>
            <li>기능은 지금과 같습니다</li>
          </ul>
        </div>
      </div>

      <div className="mt-4">
        {req.status === 'confirmed' ? (
          <p className="text-sm text-emerald-700 font-semibold">
            ✅ {status.plan_name} 신청이 본부에서 확인되었습니다 ({fmtKst(req.confirmed_at)}). {effective}부터 적용됩니다.
          </p>
        ) : req.status === 'requested' ? (
          <p className="text-sm text-blue-700 font-semibold">
            📝 {status.plan_name} 신청 완료 — {fmtKst(req.requested_at)}
            {req.requested_by_name ? ` · ${req.requested_by_name}` : ''}. {effective}부터 적용됩니다.
          </p>
        ) : status.can_request ? (
          <button
            type="button"
            onClick={openDialog}
            className="w-full sm:w-auto px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
          >
            {status.plan_name}으로 신청하기
          </button>
        ) : status.is_staff_proxy ? (
          <p className="text-sm text-gray-600">구독 신청은 원장님 본인 계정에서만 할 수 있습니다.</p>
        ) : null}
        <p className="mt-2 text-xs text-gray-500">문의: {status.support_email} 또는 [문의하기]</p>
      </div>

      {dialogOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-gray-900">{status.plan_name} 구독 신청</h3>
            <div className="mt-4 rounded-lg border-2 border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-700 font-semibold">{effective}부터</p>
              <SubscriptionPriceBlock planName={status.plan_name} price={status.price} feeLabel={status.fee_label} />
              <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc pl-5">
                <li>매월 1일 그 달 분 청구, 매월 10일까지 납부</li>
                <li>카카오 알림톡은 지금처럼 별도 — 발송 성공 건수 × 건당 13원(부가세 별도)</li>
                <li>약정 없음 · 월 단위 자동 연장 · 언제든 해지</li>
                <li>운영 회사: 주식회사 뉴어텐드</li>
              </ul>
            </div>

            <div className="mt-4 border border-gray-200 rounded-lg divide-y divide-gray-200">
              <div className="flex items-center justify-between gap-3 p-3">
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={agreedTerms}
                    onChange={(e) => setAgreedTerms(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-800">
                    <span className="text-blue-600 font-medium">[필수]</span> 서비스 구독 이용약관({effective} 시행)에 동의합니다
                  </span>
                </label>
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 underline shrink-0">
                  전문 보기
                </a>
              </div>
              <label className="flex items-center gap-3 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedPricing}
                  onChange={(e) => setAgreedPricing(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-800">
                  <span className="text-blue-600 font-medium">[필수]</span> 요금 및 결제 조건({status.plan_name} {priceOneLine(status.price, status.fee_label)}, 매월 10일 납부)에 동의합니다
                </span>
              </label>
            </div>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving || !agreedTerms || !agreedPricing}
                className="flex-1 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? '신청 중…' : '동의하고 신청'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlanCard;
